import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AlertTriangle, CheckCircle2, Calculator } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtCredits = (v) => `${Number(v || 0).toFixed(3)} cr.`;
const fmtUSD = (v) => `$${Number(v || 0).toFixed(3)}`;
const fmtEUR = (v) => `€${Number(v || 0).toFixed(3)}`;

/**
 * Diálogo que aparece antes de lanzar un análisis masivo IA.
 * Muestra coste estimado + saldo disponible y permite continuar/cancelar
 * o cambiar a un modelo más barato.
 *
 * Props:
 *  - open (bool)
 *  - onClose (fn)
 *  - onConfirm (fn) → se llama al pulsar "Continuar"
 *  - cantidad (num): nº de items del lote
 *  - modelo (str): modelo configurado actualmente
 *  - proveedor (str): proveedor configurado
 *  - avgInputChars (num, opt): default 3000
 *  - avgOutputChars (num, opt): default 800
 *  - titulo (str, opt): header personalizado
 */
export default function DialogEstimacionLote({
  open, onClose, onConfirm,
  cantidad, modelo, proveedor,
  avgInputChars = 3000, avgOutputChars = 800,
  titulo = 'Confirmar análisis IA en lote',
}) {
  const [est, setEst] = useState(null);
  const [estCheap, setEstCheap] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !modelo || !cantidad) return;
    const cargar = async () => {
      setLoading(true);
      try {
        const payload = {
          modelo, cantidad, proveedor: proveedor || 'anthropic',
          avg_input_chars: avgInputChars, avg_output_chars: avgOutputChars,
        };
        const r = await axios.post(`${API}/ia-consumo/estimar-lote`, payload);
        setEst(r.data);
        // También estimar con gpt-5.4-mini como alternativa barata (si no es ya el modelo activo)
        if (!(modelo || '').toLowerCase().includes('gpt-5.4-mini')) {
          const rc = await axios.post(`${API}/ia-consumo/estimar-lote`, {
            ...payload, modelo: 'gpt-5.4-mini', proveedor: 'openai',
          });
          setEstCheap(rc.data);
        } else {
          setEstCheap(null);
        }
      } catch (e) {
        console.error(e);
        setEst(null);
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [open, modelo, cantidad, proveedor, avgInputChars, avgOutputChars]);

  if (!est && !loading) {
    // Si no pudimos estimar, permitir continuar sin bloquear.
    return null;
  }

  const suficiente = est?.suficiente;
  const ahorroMini = est && estCheap ? est.estimado_usd - estCheap.estimado_usd : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="dialog-estimacion-lote">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-indigo-600" />
            {titulo}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-8 text-center text-slate-500">Calculando coste estimado…</div>
        )}

        {est && (
          <div className="space-y-4">
            {/* Resumen coste */}
            <div className="rounded-lg border p-4 bg-slate-50">
              <div className="text-sm text-slate-500">Vas a analizar</div>
              <div className="text-2xl font-bold text-slate-800" data-testid="est-cantidad">
                {cantidad.toLocaleString('es-ES')} elementos con <span className="text-indigo-600">{modelo}</span>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <div className="text-xs text-slate-500">Coste estimado</div>
                  <div className="text-xl font-bold text-slate-900" data-testid="est-coste-usd">
                    {fmtCredits(est.estimado_credits)}
                    <span className="text-sm font-normal text-slate-500 ml-2">
                      ({fmtUSD(est.estimado_usd)} · {fmtEUR(est.estimado_eur)})
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Saldo disponible</div>
                  <div className="text-xl font-bold text-slate-900" data-testid="est-saldo-usd">
                    {fmtCredits(est.saldo_restante_credits)}
                    <span className="text-sm font-normal text-slate-500 ml-2">({fmtUSD(est.saldo_restante_usd)})</span>
                  </div>
                </div>
              </div>
              <div className="text-xs text-slate-500 mt-3 pt-3 border-t">
                Saldo tras el lote: <strong className="text-slate-700">
                  {fmtCredits(est.saldo_despues_credits)} ({fmtUSD(est.saldo_despues_usd)})
                </strong>
              </div>
            </div>

            {/* Alerta */}
            {suficiente ? (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                <div className="text-sm text-emerald-800">
                  Tienes saldo suficiente para este lote.
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
                <div className="text-sm text-red-800">
                  <strong>Saldo insuficiente.</strong> Recarga el Emergent LLM Key
                  (Profile → Universal Key) o cambia a un modelo más barato antes
                  de continuar. Si continúas igualmente, el análisis se cortará
                  a mitad del lote.
                </div>
              </div>
            )}

            {/* Alternativa más barata */}
            {estCheap && ahorroMini > 0.005 && (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-indigo-900">💡 Alternativa más barata</span>
                  <Badge className="bg-indigo-600">{fmtCredits(estCheap.estimado_credits)}</Badge>
                </div>
                <div className="text-indigo-800">
                  Con <strong>gpt-5.4-mini</strong> ahorrarías <strong>{fmtCredits(ahorroMini / (est.credit_rate_usd || 0.20))} ({fmtUSD(ahorroMini)})</strong>
                  {' '}({((1 - estCheap.estimado_usd / est.estimado_usd) * 100).toFixed(0)}% menos).
                  Precisión similar para análisis de emails legales.
                  Cambia el modelo en <em>Configuración → IA Emails</em> antes de lanzar.
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="btn-estimacion-cancelar"
          >
            Cancelar
          </Button>
          <Button
            onClick={() => { onConfirm?.(); onClose(); }}
            className={suficiente
              ? 'bg-indigo-600 hover:bg-indigo-700'
              : 'bg-red-600 hover:bg-red-700'}
            disabled={loading}
            data-testid="btn-estimacion-continuar"
          >
            {suficiente ? 'Continuar con análisis' : 'Continuar de todos modos'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
